/**
 * Role Guard Component
 * Access control component for role-based authorization
 */

import { Card } from '../components/card';
import { useTranslation } from 'react-i18next';
import type { PortalRole } from '../api/portal-user';
import { useAuthStore } from '../services/authentication/auth-store';

interface RoleGuardProps {
  allowedRoles: PortalRole[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * RoleGuard renders children only if the authenticated user has one of the allowed roles.
 */
export function RoleGuard({ allowedRoles, children, fallback }: RoleGuardProps) {
  const user = useAuthStore((state) => state.user);

  if (!user || !allowedRoles.includes(user.role)) {
    return fallback || <AccessDenied />;
  }

  return <>{children}</>;
}

function AccessDenied() {
  const { t } = useTranslation();

  return (
    <div className="portal-shell flex items-center justify-center p-6">
      <Card framed className="max-w-md w-full text-center">
        <h1 className="tone-danger text-2xl font-bold mb-4">
          {t('accessDenied.title')}
        </h1>
        <p className="tone-text mb-4">
          {t('accessDenied.message')}
        </p>
        <p className="tone-soft text-sm">
          {t('accessDenied.contact')}
        </p>
      </Card>
    </div>
  );
}
